/**
 * FileParser Service
 * Prepares files for backend parsing
 *
 * NOTE: Email parsing is now done on the backend for security.
 * This class only handles file preparation and base64 encoding.
 */
class FileParser {
  /**
   * Validate email address format
   * @param {string} email - Email address to validate
   * @returns {boolean} True if email is valid
   */
  validateEmail(email) {
    if (!email || typeof email !== 'string') {
      return false;
    }

    // More strict validation for final check
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email.trim());
  }

  /**
   * Convert file to base64 string for backend parsing
   * @param {File} file - The file to convert
   * @returns {Promise<string>} Base64 encoded file content
   */
  async convertFileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        const dataUrl = event.target.result;
        // Remove "data:*/*;base64," prefix to get pure base64 string
        const base64 = dataUrl.split(',')[1];
        resolve(base64);
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsDataURL(file);
    });
  }

  /**
   * Prepare file data for backend parsing
   * Converts file to base64 and packages with metadata
   * @param {File} file - The file to prepare
   * @returns {Promise<Object>} File data object for API
   */
  async prepareFileForBackend(file) {
    const base64Content = await this.convertFileToBase64(file);

    return {
      fileContent: base64Content,
      fileType: 'csv',
      fileName: file.name,
      metadata: {
        fileSize: file.size,
        timestamp: new Date().toISOString()
      }
    };
  }
}

export default FileParser;
